import { ReactNode, useRef, useCallback } from 'react';
import { Center, Loader, ScrollArea, Stack } from '@mantine/core';

interface InfiniteScrollProps {
  children: ReactNode;
  onLoadMore?: () => void;
  isLoading?: boolean;
  height?: number | string;
  threshold?: number;
  disabled?: boolean;
}

export function InfiniteScroll({
  children,
  onLoadMore,
  isLoading = false,
  height = 500,
  threshold = 250,
  disabled = false
}: InfiniteScrollProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(
    ({ y }: { x: number; y: number }) => {
      const viewport = containerRef.current?.querySelector('.mantine-ScrollArea-viewport');
      if (!viewport?.clientHeight || !viewport?.scrollHeight) return;

      const hasReachedThreshold = y + viewport.clientHeight + threshold >= viewport.scrollHeight;

      if (hasReachedThreshold && onLoadMore && !isLoading && !disabled) {
        onLoadMore();
      }
    },
    [threshold, onLoadMore, isLoading, disabled]
  );

  return (
    <ScrollArea ref={containerRef} h={height} scrollbars="y" onScrollPositionChange={handleScroll}>
      <Stack gap="md">
        {children}
        {isLoading && !disabled && (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        )}
      </Stack>
    </ScrollArea>
  );
}
